// Error handlers for common React issues

// Handle ResizeObserver errors
export const handleResizeObserverError = (error: Error) => {
  // ResizeObserver errors are usually harmless and happen when components unmount
  if (error.message && error.message.includes('ResizeObserver loop limit exceeded')) {
    console.warn('ResizeObserver error (harmless):', error.message);
    return;
  }
  
  // Log other errors normally
  console.error('Unhandled error:', error);
};

// Suppress ResizeObserver errors globally
export const suppressResizeObserverErrors = () => {
  // Store original console.error
  const originalError = console.error;
  
  // Override console.error to filter ResizeObserver errors
  console.error = (...args: any[]) => {
    const message = args[0];
    
    // Check if it's a ResizeObserver error
    if (
      typeof message === 'string' && 
      (message.includes('ResizeObserver loop limit exceeded') ||
       message.includes('ResizeObserver loop completed with undelivered notifications'))
    ) {
      // Suppress these specific errors
      return;
    }
    
    // Check for the specific error you're seeing
    if (
      typeof message === 'string' &&
      message.includes('Cannot read properties of undefined (reading \'includes\')')
    ) {
      // This might be related to ResizeObserver error handling
      console.warn('Suppressed ResizeObserver-related error:', message);
      return;
    }
    
    // Call original console.error for other errors
    originalError.apply(console, args);
  };
  
  // Handle window errors
  const handleWindowError = (event: ErrorEvent) => {
    if (event.error && event.error.message) {
      const message = event.error.message;
      if (
        message.includes('ResizeObserver') ||
        message.includes('Cannot read properties of undefined (reading \'includes\')')
      ) {
        event.preventDefault();
        return;
      }
    }
  };
  
  // Handle unhandled promise rejections
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (event.reason && event.reason.message) {
      const message = event.reason.message;
      if (
        message.includes('ResizeObserver') ||
        message.includes('Cannot read properties of undefined (reading \'includes\')')
      ) {
        event.preventDefault();
        return;
      }
    }
  };
  
  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  
  // Return cleanup function
  return () => {
    console.error = originalError;
    window.removeEventListener('error', handleWindowError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  };
};

// Global error handler for ResizeObserver
export const setupGlobalErrorHandlers = () => {
  return suppressResizeObserverErrors();
};

// Debounced ResizeObserver wrapper
export const createDebounceResizeObserver = (callback: ResizeObserverCallback, delay = 100) => {
  let timeoutId: NodeJS.Timeout;
  
  return new ResizeObserver((entries, observer) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      try {
        callback(entries, observer);
      } catch (error) {
        handleResizeObserverError(error as Error);
      }
    }, delay);
  });
};